import React, { useState, useRef, useEffect } from 'react';

import { Search } from 'lucide-react';

import { InfoIcon } from '#/assets/svgs';
import { Input, Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '#/components/ui';
import { cn, debounce } from '#/utils';

const KeywordSearchInput = ({
    label,
    placeholder,
    value,
    setSearch,
    debounceDelay = 1000,
    queryKey,
    searchIcon,
    tooltip,
}) => {
    const [searchTerm, setSearchTerm] = useState(value?.[queryKey] || '');

    useEffect(() => {
        if (value !== searchTerm) {
            setSearchTerm(value || '');
        }
    }, [value]);

    const debouncedSearch = useRef(
        debounce(value => {
            setSearch(prevParams => ({
                ...prevParams,
                [queryKey]: value,
            }));
        }, debounceDelay)
    ).current;

    const handleSearchChange = e => {
        setSearchTerm(e.target.value);
        debouncedSearch(e.target.value);
    };

    return (
        <div className='w-80 relative'>
            {searchIcon && (
                <div className='absolute left-3 top-1/2 -translate-y-1/2 text-gray400 pointer-events-none'>
                    <Search className='w-5 h-5' />
                </div>
            )}
            <Input
                name={queryKey}
                placeholder={placeholder || `Search by ${label}`}
                value={searchTerm}
                autoComplete='off'
                onChange={handleSearchChange}
                className={cn(
                    'placeholder-gray400 font-inter !text-sm font-normal',
                    searchIcon ? 'pl-10' : 'pl-3',
                    tooltip ? 'pr-8 w-full' : 'pr-3 w-fit'
                )}
            />

            {tooltip && (
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div className='absolute inset-y-0 right-2 flex items-center pointer-events-auto'>
                                <InfoIcon className='w-4 h-4 text-gray-400 hover:text-gray-600 cursor-pointer' />
                            </div>
                        </TooltipTrigger>
                        <TooltipContent
                            side='bottom'
                            sideOffset={5}
                            className='relative bg-white shadow-md border font-inter text-xs font-medium text-gray700'
                        >
                            {tooltip}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            )}
        </div>
    );
};

export { KeywordSearchInput };
